import React, { useContext, useEffect, useRef, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import ReactDOMServer from "react-dom/server";

import global from "../../../scss/dhp.scss";

import AssetName from "./Common/AssetName";
import {
  Nav,
  NavItem,
  NavLink,
  Input as RInput,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Button,
  Spinner,
  Tooltip,
} from "reactstrap";
import { Icon } from "../../ui/icon";
import Crown from "../../../assets/images/ui-crown.svg";
import voiceover from "../../../assets/images/voiceover.png";
import ColorBucket from "../ContextualMenu/ColorBucket";
import { Input } from "../../ui/input";
import { changeBucketColorAccordingBackground, getCssTransformObj, hexToRGB } from "../../../_helpers/utils";
import { useContextualUpgrade } from "../../../hooks/useContextualUpgrade";
import { useCheckCompanyPlanInfo } from "../../../hooks/useCheckCompanyPlanInfo";
import { COMPANY_SUPERADMIN } from "../../../constants/company";
import { CALLOUT } from "../../../constants/editor";
import useAlignment from "../../../hooks/useAlignment";
import useAddWidget from "../../../hooks/useAddWidget";
import useElementInnerHtml from "../../../hooks/useElementInnerHtml";
import { EditorContext } from "../../../containers/editor/EditorLayout";
import { widgetConfig } from "../editor_config";
import { fetchAiVoice, playAiVoice } from "../../../store/actions/editorActions";
import { useDispatch, useSelector } from "react-redux";
import { ProcessLoader } from "../../ui/loader/processLoader";

let style = Object.assign({}, global);

const RichTextBox = props => {
  const editorRef = useRef(null);

  const [showBtn, setShowBtn] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [charCount, setCharCount] = useState(0);
  const [boldActive, setBoldActive] = useState(false);

  const MAX_LIMIT = 200;

  // Handle input + limit
  const handleInput = () => {
    let text = editorRef.current.innerText;

    if (text.length > MAX_LIMIT) {
      editorRef.current.innerText = text.substring(0, MAX_LIMIT);
      placeCursorAtEnd(editorRef.current);
      text = editorRef.current.innerText;
    }

    setCharCount(text.length);
    props.setCalloutTextConfig({
      ...props.calloutTextConfig,
      text: document.getElementById("callOutTextarea").innerHTML,
    });
  };

  // Selection logic
  const handleSelection = () => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      setShowBtn(false);
      return;
    }

    const text = selection.toString().trim();
    if (!text) {
      setShowBtn(false);
      return;
    }

    const range = selection.getRangeAt(0);

    if (!editorRef.current.contains(range.commonAncestorContainer)) {
      setShowBtn(false);
      return;
    }

    const rect = range.getBoundingClientRect();

    setPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });

    setShowBtn(true);
    setBoldActive(isBoldActive());
  };

  const handleBold = () => {
    document.execCommand("bold");
    setBoldActive(isBoldActive()); // update state
    props.setCalloutTextConfig({
      ...props.calloutTextConfig,
      text: document.getElementById("callOutTextarea").innerHTML,
    });
  };

  const isBoldActive = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;

    let node = selection.anchorNode;

    while (node && node !== editorRef.current) {
      if (node.nodeName === "B" || node.nodeName === "STRONG") {
        return true;
      }
      node = node.parentNode;
    }

    return false;
  };

  const placeCursorAtEnd = el => {
    el.focus();
    document.execCommand("selectAll", false, null);
    document.getSelection().collapseToEnd();
  };

  const isEmpty = () => {
    const el = editorRef.current;
    if (!el) return true;

    return el.innerText.trim().length === 0;
  };

  const handleKeyDown = e => {
    // Check for Ctrl (Windows/Linux) or Cmd (Mac)
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
      e.preventDefault(); // prevent browser default behavior

      const selection = window.getSelection();

      // Only apply if selection exists inside editor
      if (selection && selection.rangeCount > 0 && editorRef.current.contains(selection.anchorNode)) {
        handleBold();
      }
    }
  };

  const handlePaste = e => {
    e.preventDefault();

    const text = e.clipboardData.getData("text/plain");

    document.execCommand("insertText", false, text);

    // Update character count and config
    handleInput();
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("keyup", handleSelection);
    document.addEventListener("selectionchange", handleSelection);

    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("keyup", handleSelection);
      document.removeEventListener("selectionchange", handleSelection);
    };
  }, []);

  // set exsisitng text into conenet editable div
  useEffect(() => {
    if (editorRef.current) {
      const currentHTML = editorRef.current.innerHTML;
      const newHTML = props.calloutTextConfig.text || "";

      // Only update if different (VERY IMPORTANT)
      if (currentHTML !== newHTML) {
        editorRef.current.innerHTML = newHTML;

        const text = editorRef.current.innerText || "";
        setCharCount(text.length);
      }
    }
  }, [props.calloutTextConfig.text]);

  return (
    <div className={style["me-20"]}>
      {/* Label */}
      <h6 className={style["fw-6"]}>Callout Text</h6>

      {/* Floating Bold Button */}
      {showBtn && (
        <div
          className={cx(style["bold"], {
            [style["active"]]: boldActive,
          })}
          style={{
            top: pos.top,
            left: pos.left,
          }}
          onMouseDown={e => e.preventDefault()}
          onClick={handleBold}>
          <span>
            <Icon icon="bold2" />
          </span>
        </div>
      )}

      {/* Fixed Height Editor */}
      <div className={style["callOutTextarea"]}>
        <div
          id="callOutTextarea"
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={style["Textarea"]}></div>

        {/* Placeholder goes HERE */}
        {isEmpty() && <span className={style["placeholder"]}>Explain the step</span>}
      </div>

      {/* Character Count */}
      <div className={style["character-count"]}>
        {charCount}/{MAX_LIMIT}
      </div>
    </div>
  );
};
RichTextBox.propTypes = {
  calloutTextConfig: PropTypes.obj,
  setCalloutTextConfig: PropTypes.func,
};

const AiVoice = props => {
  let { metadata } = useContext(EditorContext);

  const region = useSelector(state => state.auth?.company?.cluster_code?.toLowerCase() || null);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const generateRequestRef = useRef(0);
  const previewRequestRef = useRef(0);
  const lastGeneratedRef = useRef({
    text: "",
    voiceId: "",
  });

  const dispatch = useDispatch();
  const showUpgrade = useContextualUpgrade();
  const paidCompanyInfo = useCheckCompanyPlanInfo(true);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [genderDropdown, setGenderDropdown] = useState(false);
  const [accentDropdown, setAccentDropdown] = useState(false);
  const [availableVoices, setAvailableVoices] = useState();
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [filterCount, setFilterCount] = useState();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [gender, setGender] = useState("All");
  const [accent, setAccent] = useState("All");
  const [disableFilterForm, setDisableFilterForm] = useState(true);
  const [loadingVoiceId, setLoadingVoiceId] = useState(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [audioInstance, setAudioInstance] = useState(null);

  let availableGenders = ["All", "Male", "Female"];
  let availableAccents = ["All", "American", "British", "Australian", "Indian"];

  const toggle2 = () => setDropdownOpen(prevState => !prevState);

  const toggle3 = () => setFilterDropdownOpen(prevState => !prevState);

  const toggle4 = () => setGenderDropdown(prevState => !prevState);

  const toggle5 = () => setAccentDropdown(prevState => !prevState);

  const upgradeSubscriptionCheck = () => {
    if (paidCompanyInfo?.companyRole === COMPANY_SUPERADMIN && !paidCompanyInfo?.isPaid) {
      showUpgrade("aiVoice", voiceover);
      return;
    } else {
      // if voice opver is disable -> enable voice over and set 1st available voice
      if (!props.calloutTextConfig.enableVoiceOver) {
        handleVoiceSelect(availableVoices[0]);
      }
      // else if voice opver is enable ->  disable voice over
      else {
        props.setCalloutTextConfig({
          ...props.calloutTextConfig,
          enableVoiceOver: false,
        });

        resetFilters();
      }
    }
  };

  const applyFilter = () => {
    filterVoices(searchKeyword, gender, accent);
    setDisableFilterForm(true);
    setFilterDropdownOpen(false);
  };

  const applySearch = keyword => {
    setSearchKeyword(keyword);
    filterVoices(keyword, gender, accent);
  };

  const filterVoices = (keyword, genderValue, accentValue) => {
    let filtered = availableVoices;

    // search filter
    if (keyword) {
      filtered = filtered.filter(voice => voice.display_name.toLowerCase().includes(keyword.toLowerCase()));
    }

    // gender filter
    if (genderValue !== "All") {
      filtered = filtered.filter(voice => voice.gender.toLowerCase() === genderValue.toLowerCase());
    }

    // accent filter (only if your API actually has accent field)
    if (accentValue !== "All") {
      filtered = filtered.filter(voice => voice.accent?.toLowerCase() === accentValue.toLowerCase());
    }

    setFilteredVoices(filtered);
  };

  const handleVoiceSelect = availableVoice => {
    props.setIsGeneratingAudio(true);

    props.setCalloutTextConfig({
      ...props.calloutTextConfig,
      enableVoiceOver: true,
      voiceOverValue: availableVoice.display_name,
    });

    // stop any playing preview
    if (audioInstance) {
      audioInstance.pause();
      setPlayingVoiceId(null);
    }

    let payload = {
      prompt: props.calloutTextConfig.text !== "" ? props.calloutTextConfig.text : "Explain the step",
      voice_id: availableVoice.voice_id,
    };

    const requestId = ++generateRequestRef.current;

    dispatch(playAiVoice(payload, region)).then(r => {
      // ignore if not latest
      if (requestId !== generateRequestRef.current) return;

      props.setIsGeneratingAudio(false);

      lastGeneratedRef.current = {
        text: payload.prompt,
        voiceId: payload.voice_id,
      };

      props.setCalloutTextConfig({
        ...props.calloutTextConfig,
        enableVoiceOver: true,
        voiceOverValue: availableVoice.display_name,
        voiceOverAudioFile: r.data.audio_url,
      });
    });

    setSearchKeyword(""); // clear search input
    filterVoices("", gender, accent); // keep filters, reset only search
  };

  const handleVoicePreview = availableVoice => {
    const requestId = ++previewRequestRef.current;

    const currentText = props.calloutTextConfig.text || "Explain the step";

    const currentVoiceId = availableVoice.voice_id;

    // If same voice is already playing → pause
    if (playingVoiceId === currentVoiceId && audioInstance) {
      audioInstance.pause();
      setPlayingVoiceId(null);
      return;
    }

    // Stop previous audio
    if (audioInstance) {
      audioInstance.pause();
    }

    const isSameRequest =
      lastGeneratedRef.current.text === currentText && lastGeneratedRef.current.voiceId === currentVoiceId;

    // CASE 1: Use cached audio (NO API CALL)
    if (isSameRequest && props.calloutTextConfig.voiceOverAudioFile) {
      const audio = new Audio(props.calloutTextConfig.voiceOverAudioFile);

      setAudioInstance(audio);
      setPlayingVoiceId(currentVoiceId);

      audio.play();

      audio.onended = () => {
        setPlayingVoiceId(null);
      };

      return;
    }

    // CASE 2: Need API call
    setLoadingVoiceId(currentVoiceId);

    let payload = {
      prompt: currentText,
      voice_id: currentVoiceId,
    };

    dispatch(playAiVoice(payload, region)).then(r => {
      if (requestId !== previewRequestRef.current) {
        setLoadingVoiceId(null); // stop loader
        return;
      }

      const audioUrl = r.data.audio_url;

      // store for reuse
      lastGeneratedRef.current = {
        text: currentText,
        voiceId: currentVoiceId,
      };

      props.setCalloutTextConfig(prev => ({
        ...prev,
        voiceOverAudioFile: audioUrl,
      }));

      const audio = new Audio(audioUrl);

      setAudioInstance(audio);
      setLoadingVoiceId(null);
      setPlayingVoiceId(currentVoiceId);

      audio.play();

      audio.onended = () => {
        setPlayingVoiceId(null);
      };
    });
  };

  const resetFilters = () => {
    setGender("All");
    setAccent("All");
    setSearchKeyword("");
    setFilterCount(0);

    // reset voice list also
    if (availableVoices) {
      setFilteredVoices(availableVoices);
    }

    setDisableFilterForm(true);
  };

  const selectedVoice = availableVoices?.find(
    v => v.display_name.toLowerCase() === props.calloutTextConfig.voiceOverValue?.toLowerCase()
  );

  useEffect(() => {
    const handleOutsideClick = e => {
      if (!containerRef.current) return;

      // click outside component
      if (!containerRef.current.contains(e.target)) {
        if (audioInstance) {
          audioInstance.pause();
          audioInstance.currentTime = 0;
          setPlayingVoiceId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [audioInstance]);

  useEffect(() => {
    return () => {
      if (audioInstance) {
        audioInstance.pause();
      }
    };
  }, [audioInstance]);

  useEffect(() => {
    if (!dropdownOpen && audioInstance) {
      audioInstance.pause();
      setPlayingVoiceId(null);
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (!props.calloutTextConfig.enableVoiceOver) return;
    if (!selectedVoice) return;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    // clear previous timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // set new timer
    debounceRef.current = setTimeout(() => {
      props.setIsGeneratingAudio(true);
      let payload = {
        prompt: props.calloutTextConfig.text !== "" ? props.calloutTextConfig.text : "Explain the step",
        voice_id: selectedVoice.voice_id,
      };

      const requestId = ++generateRequestRef.current;

      dispatch(playAiVoice(payload, region)).then(r => {
        if (requestId !== generateRequestRef.current) {
          setLoadingVoiceId(null);
          return;
        }

        props.setIsGeneratingAudio(false);

        lastGeneratedRef.current = {
          text: payload.prompt,
          voiceId: payload.voice_id,
        };

        props.setCalloutTextConfig(prev => ({
          ...prev,
          voiceOverAudioFile: r.data.audio_url,
        }));
      });
    }, 800); // delay (adjust if needed)

    return () => clearTimeout(debounceRef.current);
  }, [props.calloutTextConfig.text]);

  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [props.calloutTextConfig.voiceOverAudioFile]);

  useEffect(() => {
    if (gender !== "All" && accent !== "All") setFilterCount(2);
    else if (gender === "All" && accent === "All") setFilterCount(0);
    else setFilterCount(1);
  }, [gender, accent]);

  useEffect(() => {
    const isCalloutSelected = metadata.activeWidgetId?.length === 1 && metadata.activeWidgetType?.[0] === CALLOUT;

    if (!isCalloutSelected) {
      // invalidate all pending requests
      generateRequestRef.current++;
      previewRequestRef.current++;

      // stop loader
      setLoadingVoiceId(null);
      props.setIsGeneratingAudio(false);

      // stop audio
      if (audioInstance) {
        audioInstance.pause();
        audioInstance.currentTime = 0;
        setPlayingVoiceId(null);
      }

      resetFilters();
    }
  }, [metadata.activeWidgetId[0]]);

  useEffect(() => {
    dispatch(fetchAiVoice()).then(r => {
      setAvailableVoices(r.data.data.voices);
      setFilteredVoices(r.data.data.voices); // default show all
    });
  }, []);

  return (
    <div ref={containerRef} className={style["me-20"]}>
      <h6 className={cx(style["fw-6"], style["d-flex"], style["align-items-center"], style["mb-3"])}>
        AI Voiceover
        <span className={cx(style["crown-icon"], style["ms-2"], style["wh-22"])}>
          <img
            src={Crown}
            onError={e => {
              e.target.onerror = null;
              e.target.src = Crown;
            }}
            className={style["img-fluid"]}
            alt="crown"
          />
        </span>
      </h6>

      <div
        className={cx(
          style["custom-control"],
          style["custom-switch"],
          style["form-switch"],
          style["form-check"],
          style["mb-3"]
        )}>
        <RInput
          type="checkbox"
          className={cx(style["custom-control-input"], style["form-check-input"])}
          checked={props.calloutTextConfig.enableVoiceOver}
          id="customSwitchForVoiceOver"
          onChange={upgradeSubscriptionCheck}
        />

        <label
          className={cx(style["custom-control-label"], style["form-check-label"], style["fw-4"], style["font-15"])}
          htmlFor="customSwitchForVoiceOver">
          Use AI Voiceover
        </label>
      </div>

      {props.calloutTextConfig.enableVoiceOver && (
        <div className={cx(style["line-controls-wrap"], style["flex-column"], style["align-items-start"])}>
          <div
            className={cx(
              style["d-flex"],
              style["align-items-center"],
              style["justify-content-between"],
              style["w-100"],
              style["mb-1"]
            )}>
            <div className={cx(style["fw-6"], style["font-base"])}> Voice</div>
            <div className={cx(style["d-flex"], style["align-items-center"])}>
              {/* Play voice button */}
              <span className={cx(style["custom-tooltip"], style["d-flex"], style["cursor-pointer"])}>
                {loadingVoiceId === selectedVoice?.voice_id ? (
                  <Spinner size="sm" color="dark" className={style["ms-2"]} />
                ) : playingVoiceId === selectedVoice?.voice_id ? (
                  <Icon icon="ui-circled-pause" onClick={() => handleVoicePreview(selectedVoice)} />
                ) : (
                  <Icon icon="ui-circled-play" onClick={() => handleVoicePreview(selectedVoice)} />
                )}

                <div className={cx(style["custom-tooltip-content"], style["top"])}>Play</div>
              </span>

              {/* Filter area */}
              <Dropdown
                className={cx(style["more-action"], style["ms-1"], style["filter-area"])}
                isOpen={filterDropdownOpen}
                toggle={toggle3}>
                <DropdownToggle>
                  {filterCount > 0 && <span className={style["filter-count"]}>{filterCount}</span>}
                </DropdownToggle>

                <DropdownMenu
                  container="body"
                  className={cx(style["dropdown-menu-right"], style["border-0"], style["shadow-sm"], style["p-3"])}
                  style={{ width: "242px" }}
                  placement="bottom-start"
                  end>
                  {/* gender dropdown */}
                  <div className={cx(style["line-controls-wrap"], style["flex-column"], style["align-items-start"])}>
                    <div className={cx(style["fw-6"], style["w-100"], style["font-base"])}>Gender</div>
                    <Dropdown className={cx(style["border-style"])} isOpen={genderDropdown} toggle={toggle4}>
                      <DropdownToggle className={style["text-capitalize"]}>
                        {gender}
                        <Icon icon="ui-arrow-down" />
                      </DropdownToggle>

                      <DropdownMenu className={cx(style["border-0"], style["shadow"])}>
                        {availableGenders.map(availableGender => (
                          <div className={cx(style["item-holder"])} key={availableGender}>
                            <DropdownItem
                              className={cx(style["text-capitalize"], {
                                [style["active"]]: availableGender === gender,
                              })}
                              tag="a"
                              onClick={() => {
                                setGender(availableGender);
                                setDisableFilterForm(false);
                              }}>
                              {availableGender}
                            </DropdownItem>
                          </div>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>

                  {/* accent dropdown */}
                  <div className={cx(style["line-controls-wrap"], style["flex-column"], style["align-items-start"])}>
                    <div className={cx(style["fw-6"], style["w-100"], style["font-base"])}> Accent</div>
                    <Dropdown className={cx(style["border-style"])} isOpen={accentDropdown} toggle={toggle5}>
                      <DropdownToggle className={style["text-capitalize"]}>
                        {accent}
                        <Icon icon="ui-arrow-down" />
                      </DropdownToggle>

                      <DropdownMenu className={cx(style["border-0"], style["shadow"])}>
                        {availableAccents.map(availableAccent => (
                          <div className={cx(style["item-holder"])} key={availableAccent}>
                            <DropdownItem
                              className={cx(style["text-capitalize"], {
                                [style["active"]]: availableAccent === accent,
                              })}
                              tag="a"
                              onClick={() => {
                                setAccent(availableAccent);
                                setDisableFilterForm(false);
                              }}>
                              {availableAccent}
                            </DropdownItem>
                          </div>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>

                  {/* apply filter button */}
                  <Button
                    type="submit"
                    color="primary"
                    className={style["btn-block"]}
                    cssModule={style}
                    disabled={disableFilterForm}
                    onClick={() => applyFilter()}>
                    Apply Filters
                  </Button>
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>

          {/* Ai Voice dropdown */}
          <Dropdown
            isOpen={dropdownOpen}
            toggle={toggle2}
            className={cx(style["border-style"], style["max-w-none"], style["voice-dropdown"])}>
            <DropdownToggle className={style["text-capitalize"]}>
              {props.calloutTextConfig.voiceOverValue}
              <Icon icon="ui-arrow-down" />
            </DropdownToggle>

            <DropdownMenu
              className={cx(style["shadow"], style["border-0"], style["rounded"])}
              strategy="fixed"
              style={{ width: "242px", zIndex: 9999 }}
              modifiers={[
                {
                  name: "preventOverflow",
                  options: { boundary: "viewport" },
                },
              ]}>
              <div className={cx(style["search"], style["px-3"], style["pt-2"])}>
                <Input
                  cssModule={style}
                  returnType="inputGroup"
                  addonType="prepend"
                  type="search"
                  id="search-category"
                  name="search-category"
                  onChange={e => applySearch(e.target.value.trim())}
                  autoFocus={true}
                />
              </div>

              <div className={cx(style["customScroll"], style["scroll-Y"])}>
                {filteredVoices?.length > 0 ? (
                  filteredVoices.map(availableVoice => (
                    <div className={cx(style["item-holder"])} key={availableVoice.voice_id}>
                      <DropdownItem
                        className={cx(style["text-capitalize"], {
                          [style["active"]]: availableVoice.display_name === props.calloutTextConfig.voiceOverValue,
                        })}
                        tag="a"
                        // onClick={() => handleVoiceClick(availableVoice)}
                        onClick={() => handleVoiceSelect(availableVoice)} // closes dropdown
                      >
                        <div
                          className={cx(style["audio-play-btn"], style["d-flex"], style["pe-1"])}
                          onClick={e => {
                            e.stopPropagation(); // prevent dropdown close
                            handleVoicePreview(availableVoice);
                          }}>
                          {loadingVoiceId === availableVoice.voice_id ? (
                            <Spinner size="sm" color="dark" className={style["ms-2"]} />
                          ) : playingVoiceId === availableVoice.voice_id ? (
                            <Icon icon={"ui-circled-pause"} /> // when playing
                          ) : (
                            <Icon icon={"ui-circled-play"} /> // default
                          )}
                        </div>

                        <div className={cx(style["ms-2"], style["w-75"])}>
                          <h6 className={cx(style["audio-title"], style["text-truncate"], style["mb-0"])}>
                            {availableVoice.display_name}
                          </h6>

                          <div className={cx(style["audio-duration"], style["text-muted"], style["font-13"])}>
                            {availableVoice.gender}
                          </div>
                        </div>
                      </DropdownItem>
                    </div>
                  ))
                ) : (
                  <div className={cx(style["p-2"], style["text-center"], style["text-muted"])}>No matches found</div>
                )}
              </div>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}
    </div>
  );
};
AiVoice.propTypes = {
  calloutTextConfig: PropTypes.obj,
  setCalloutTextConfig: PropTypes.func,
  setIsGeneratingAudio: PropTypes.func,
};

const CalloutText = props => {
  return (
    props.calloutTextConfig && (
      <>
        <RichTextBox {...props} />
        <AiVoice {...props} />
      </>
    )
  );
};
CalloutText.propTypes = {
  calloutTextConfig: PropTypes.obj,
  setCalloutTextConfig: PropTypes.func,
};

const CalloutSettings = props => {
  let { metadata } = useContext(EditorContext);
  let fonts = JSON.parse(localStorage?.getItem("allFonts"));
  let availableFonts = fonts.filter(font => font.bold_weight !== "");

  const [defaultValue, setDefaultValue] = useState();
  const [backgroundColor, setBackgroundColor] = useState();
  const [backgroundBucketColor, setBackgroundBucketColor] = useState();
  const [backgroundEyeDropperColor, setBackgroundEyeDropperColor] = useState();
  const [textColor, setTextColor] = useState();
  const [textBucketColor, setTextBucketColor] = useState();
  const [textEyeDropperColor, setTextEyeDropperColor] = useState();
  const [loadCureentFormatting, setLoadCureentFormatting] = useState();
  const [updateContext, setUpdateContext] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [widthDropdownOpen, setWidthDropdownOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const availablePositions = ["Top", "Bottom", "Left", "Right"];
  const availableWidths = ["Small", "Medium", "Large"];

  const toggle2 = () => setDropdownOpen(prevState => !prevState);

  const toggle3 = () => setWidthDropdownOpen(prevState => !prevState);

  const toggleTooltip = () => setTooltipOpen(prev => !prev);

  const updateFontFamily = fontFamily => {
    props.setCalloutTextConfig({
      ...props.calloutTextConfig,
      style: { ...props.calloutTextConfig.style, fontFamily: fontFamily },
    });
  };

  // update Background color
  useEffect(() => {
    let backgroundColorRange = changeBucketColorAccordingBackground(hexToRGB(backgroundColor));

    props.setCalloutTextConfig({
      ...props.calloutTextConfig,
      style: { ...props.calloutTextConfig.style, backgroundColor: backgroundColor },
    });

    backgroundColor && setBackgroundBucketColor(backgroundColorRange);
  }, [backgroundColor]);

  // update Background color using eye dropper
  useEffect(() => {
    setUpdateContext(true);
    setLoadCureentFormatting(false);
    setBackgroundColor(backgroundEyeDropperColor);
  }, [backgroundEyeDropperColor]);

  // update text color
  useEffect(() => {
    let textColorRange = changeBucketColorAccordingBackground(hexToRGB(textColor));

    props.setCalloutTextConfig({
      ...props.calloutTextConfig,
      style: { ...props.calloutTextConfig.style, color: textColor },
    });

    textColor && setTextBucketColor(textColorRange);
  }, [textColor]);

  // update text color using eye dropper
  useEffect(() => {
    setUpdateContext(true);
    setLoadCureentFormatting(false);
    setTextColor(textEyeDropperColor);
  }, [textEyeDropperColor]);

  // Load current formatting
  useEffect(() => {
    setLoadCureentFormatting(true);
    setDefaultValue({ name: props.calloutTextConfig.style.fontFamily });
    setBackgroundColor(props.calloutTextConfig.style.backgroundColor);
    setTextColor(props.calloutTextConfig.style.color);
  }, []);

  // Load current formatting on active widget change
  useEffect(() => {
    if (
      metadata.activeWidgetId.length === 1 &&
      metadata.activeWidgetId[0] &&
      metadata.activeWidgetType[0] === CALLOUT
    ) {
      let configData = JSON.parse(document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-config"));

      setDefaultValue({ name: configData.style.fontFamily });
      setBackgroundColor(configData.style.backgroundColor);
      setTextColor(configData.style.color);
    }
  }, [metadata.activeWidgetId[0]]);

  return (
    <>
      {/* Color section */}
      <div className={cx(style["pe-3"], style["customScroll"], style["scroll-Y"])}>
        <h6 className={style["fw-6"]}>Callout Color</h6>

        <div className={cx(style["line-controls-wrap"], style["mb-2"])}>
          <div className={cx(style["slidelabel"], style["ps-0"])}>Callout Background</div>
          <ColorBucket
            color={backgroundColor}
            setColor={setBackgroundColor}
            iconBucketColor={backgroundBucketColor}
            position={"toolbar"}
            eyedropperColor={backgroundEyeDropperColor}
            setEyedropperColor={setBackgroundEyeDropperColor}
            setLoadCurrentFormatting={setLoadCureentFormatting}
            setUpdateContext={setUpdateContext}
            source={CALLOUT}
          />
        </div>

        <div className={cx(style["line-controls-wrap"])}>
          <div className={cx(style["slidelabel"], style["ps-0"])}>Callout Text </div>
          <ColorBucket
            color={textColor}
            setColor={setTextColor}
            iconBucketColor={textBucketColor}
            position={"toolbar"}
            eyedropperColor={textEyeDropperColor}
            setEyedropperColor={setTextEyeDropperColor}
            setLoadCurrentFormatting={setLoadCureentFormatting}
            setUpdateContext={setUpdateContext}
            source={CALLOUT}
          />
        </div>

        {/* Font Section */}
        <div className={cx(style["toolset-item"], style["family-wrap"], style["font-common-action"])}>
          <div className={cx(style["slidelabel"], style["fw-6"], style["mb-1"], style["font-base"])}>Callout Font </div>
          <div>
            <Input
              type="dropdown"
              name="family"
              classNamePrefix="select"
              defaultValue={defaultValue}
              getOptionValue={option => option.name}
              getOptionLabel={option => <span style={{ fontFamily: option.name }}>{option.name}</span>}
              options={availableFonts}
              updateState={option => updateFontFamily(option.name)}
              noOptionsMessage={() => "No results found"}
            />
          </div>
        </div>

        {/* Callout Width */}
        <div className={cx(style["line-controls-wrap"], style["flex-column"], style["align-items-start"])}>
          <div
            className={cx(
              style["slidelabel"],
              style["fw-6"],
              style["w-100"],
              style["p-0"],
              style["m-0"],
              style["font-base"]
            )}>
            Callout Width
          </div>
          <Dropdown
            isOpen={widthDropdownOpen}
            toggle={toggle3}
            className={cx(style["border-style"], style["max-w-none"])}>
            <DropdownToggle className={style["text-capitalize"]}>
              {props.calloutTextConfig.callOutWidth}
              <Icon icon="ui-arrow-down" />
            </DropdownToggle>

            <DropdownMenu className={cx(style["shadow"], style["border-0"], style["rounded"])}>
              {availableWidths.map(availableWidth => (
                <React.Fragment key={availableWidth}>
                  <DropdownItem
                    className={cx(style["text-capitalize"], {
                      [style["active"]]: availableWidth === props.calloutTextConfig.callOutWidth,
                    })}
                    tag="a"
                    onClick={() => {
                      props.setCalloutTextConfig({
                        ...props.calloutTextConfig,
                        callOutWidth: availableWidth,
                      });
                    }}>
                    {availableWidth}
                  </DropdownItem>
                </React.Fragment>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        {/* Callout Position */}
        <div className={cx(style["line-controls-wrap"], style["flex-column"], style["align-items-start"])}>
          <div
            className={cx(
              style["slidelabel"],
              style["fw-6"],
              style["w-100"],
              style["p-0"],
              style["m-0"],
              style["font-base"]
            )}>
            Callout Position
          </div>
          <Dropdown isOpen={dropdownOpen} toggle={toggle2} className={cx(style["border-style"], style["max-w-none"])}>
            <DropdownToggle className={style["text-capitalize"]}>
              {props.calloutTextConfig.callOutPosition}
              <Icon icon="ui-arrow-down" />
            </DropdownToggle>

            <DropdownMenu className={cx(style["shadow"], style["border-0"], style["rounded"])}>
              {availablePositions.map(availablePosition => (
                <React.Fragment key={availablePosition}>
                  <DropdownItem
                    className={cx(style["text-capitalize"], {
                      [style["active"]]: availablePosition === props.calloutTextConfig.callOutPosition,
                    })}
                    tag="a"
                    onClick={() => {
                      props.setCalloutTextConfig({
                        ...props.calloutTextConfig,
                        callOutPosition: availablePosition,
                      });
                    }}>
                    {availablePosition}
                  </DropdownItem>
                </React.Fragment>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        {/* Callout Animation */}
        <div
          className={cx(
            style["custom-control"],
            style["custom-switch"],
            style["form-check"],
            style["form-switch"],
            style["mb-3"],
            style["tool-info"]
          )}>
          <RInput
            type="checkbox"
            className={cx(style["custom-control-input"], style["form-check-input"])}
            defaultChecked={props.calloutTextConfig.animation}
            id="customSwitchForAnimation"
            value={props.calloutTextConfig.animation}
            onChange={() => {
              props.setCalloutTextConfig({
                ...props.calloutTextConfig,
                animation: !props.calloutTextConfig.animation,
              });
            }}
          />

          <label
            className={cx(
              style["custom-control-label"],
              style["form-check-label"],
              style["fw-normal"],
              style["font-15"]
            )}
            htmlFor="customSwitchForAnimation">
            Pan and Zoom{" "}
            <span id="panZoomInfoIcon">
              <Icon icon="information" additionalclass="ms-2 font-base" />
            </span>
          </label>

          <Tooltip placement="bottom" isOpen={tooltipOpen} target="panZoomInfoIcon" toggle={toggleTooltip}>
            Pan and Zoom effect on the callout area
          </Tooltip>
        </div>
      </div>
    </>
  );
};
CalloutSettings.propTypes = {
  calloutTextConfig: PropTypes.obj,
  setCalloutTextConfig: PropTypes.func,
};

const CalloutView = props => {
  const getPositionStyle = () => {
    switch (props.calloutTextConfig.callOutPosition) {
      case "Top":
        return {
          wrapper: {
            paddingTop: "60px", // create space INSIDE
          },
          pointer: {
            top: "60px",
            left: "50%",
            transform: "translate(-50%, -100%)",
          },
          triangle: {
            borderLeft: "20px solid transparent",
            borderRight: "20px solid transparent",
            borderBottom: `20px solid ${props.calloutTextConfig.style.backgroundColor}`,
          },
          circle: {
            top: "60px",
            left: "50%",
            transform: "translate(-50%, -200%)",
          },
        };

      case "Bottom":
        return {
          pointer: {
            bottom: "-19px",
            left: "50%",
            transform: "translateX(-50%)",
          },
          triangle: {
            borderLeft: "20px solid transparent",
            borderRight: "20px solid transparent",
            borderTop: `20px solid ${props.calloutTextConfig.style.backgroundColor}`,
          },
          circle: {
            bottom: "-60px",
            left: "50%",
            transform: "translateX(-50%)",
          },
        };

      case "Left":
        return {
          pointer: {
            left: "-19px",
            top: "50%",
            transform: "translateY(-50%)",
          },
          triangle: {
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            borderRight: `20px solid ${props.calloutTextConfig.style.backgroundColor}`,
          },
          circle: {
            left: "-60px",
            top: "50%",
            transform: "translateY(-50%)",
          },
        };

      case "Right":
        return {
          pointer: {
            right: "-19px",
            top: "50%",
            transform: "translateY(-50%)",
          },
          triangle: {
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            borderLeft: `20px solid ${props.calloutTextConfig.style.backgroundColor}`,
          },
          circle: {
            right: "-60px",
            top: "50%",
            transform: "translateY(-50%)",
          },
        };

      default:
        return {};
    }
  };

  const pos = getPositionStyle();

  return (
    <div className={style["callout"]}>
      <div
        className={cx(`callout-${props.calloutTextConfig.callOutPosition}`)}
        style={{
          position: "relative",
          width: props.calloutWidthConfig[props.calloutTextConfig.callOutWidth],
          ...pos.wrapper,
        }}>
        {/* Box */}
        <div
          className="callout-text"
          style={{
            background: props.calloutTextConfig.style.backgroundColor,
            color: props.calloutTextConfig.style.color,
            fontFamily: props.calloutTextConfig.style.fontFamily,
          }}>
          <div dangerouslySetInnerHTML={{ __html: props.calloutTextConfig.text }} />
          <div className={cx(style["d-flex"], style["justify-content-end"], style["mt-3"])}>
            <div
              className={cx(style["indicator"], {
                [style["dark"]]:
                  changeBucketColorAccordingBackground(hexToRGB(props.calloutTextConfig.style.backgroundColor)) ===
                  "light",
              })}>
              <span>
                <Icon icon="ui-arrow-left2" />
              </span>
              <span>
                <Icon icon="ui-arrow-right2" />
              </span>
            </div>
          </div>
        </div>

        {/* Triangle Pointer */}
        <div
          className="callout-pointer"
          style={{
            ...pos.pointer,
            ...pos.triangle,
          }}
        />

        {/* Circle */}
        <div
          className={cx("callout-circle")}
          style={{
            background: props.calloutTextConfig.style.backgroundColor,
            ...pos.circle,
          }}
        />
      </div>
    </div>
  );
};
CalloutView.propTypes = {
  calloutTextConfig: PropTypes.obj,
  calloutWidthConfig: PropTypes.obj,
};

const CallOut = props => {
  let { metadata, widgets, updateWidgets, pageNodes } = useContext(EditorContext);

  const [activeTab, setActiveTab] = useState(0);
  const [calloutTextConfig, setCalloutTextConfig] = useState();
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);
  const [dataparam, setDataParam] = useState();
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [widgetWidth, setWidgetWidth] = useState();
  const [widgetHeight, setWidgetHeight] = useState();
  const [customCss, setCustomCss] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  const { postion: getPosition } = useAlignment(addWidgetClicked, widgetWidth, widgetHeight, "middle-center");
  const getNewWidgetObject = useAddWidget(addWidgetClicked, CALLOUT, dataparam, getPosition, widgetWidth, widgetHeight);
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    CALLOUT,
    assetInnerContent,
    dataparam,
    getNewWidgetObject,
    customCss
  );

  let defaultConfig = {
    text: "",
    style: {
      backgroundColor: "#4472c4",
      color: "#ffffff",
      fontFamily: "Poppins",
    },
    enableVoiceOver: false,
    voiceOverValue: "",
    voiceOverAudioFile: "",
    callOutWidth: "Medium",
    callOutPosition: "Top",
    animation: true,
  };
  let calloutWidthConfig = {
    Small: "300px",
    Medium: "500px",
    Large: "800px",
  };

  // load current formatting
  useEffect(() => {
    if (metadata.activeWidgetId.length === 1 && metadata.activeWidgetId[0] && metadata.activeWidgetType[0] === CALLOUT)
      fetchExistingData(); // update time fetch exsisting
    else setCalloutTextConfig(defaultConfig); // insert time set default one
  }, []);

  // load current formatting when active widget change
  useEffect(() => {
    if (metadata.activeWidgetId.length === 1 && metadata.activeWidgetId[0] && metadata.activeWidgetType[0] === CALLOUT)
      fetchExistingData(); // update time fetch exsisting
    else setCalloutTextConfig(defaultConfig);
  }, [metadata.activeWidgetId]);

  // detect if any changes in callout widget during update to disable or enable update button
  useEffect(() => {
    if (
      calloutTextConfig &&
      metadata.activeWidgetId.length === 1 &&
      metadata.activeWidgetId[0] &&
      metadata.activeWidgetType[0] === CALLOUT
    ) {
      const prev = JSON.parse(document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-config"));
      const curr = calloutTextConfig;

      let hasChanged = false;

      Object.keys(curr).forEach(key => {
        if (typeof curr[key] === "object" && curr[key] !== null) {
          Object.keys(curr[key]).forEach(innerKey => {
            const prevVal = prev[key]?.[innerKey];
            const currVal = curr[key][innerKey];

            if (prevVal === undefined && currVal !== undefined) return;

            if (currVal !== prevVal) {
              hasChanged = true;
            }
          });
        } else {
          const prevVal = prev[key];
          const currVal = curr[key];

          if (prevVal === undefined && currVal !== undefined) return;

          if (currVal !== prevVal) {
            hasChanged = true;
          }
        }
      });

      // FINAL LOGIC
      // disable if NO changes OR audio is generating
      setIsDisable(!hasChanged || isGeneratingAudio);
    }
  }, [calloutTextConfig, isGeneratingAudio]);

  const fetchExistingData = () => {
    let configData = JSON.parse(document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-config"));
    setCalloutTextConfig({
      text: configData.text,
      style: {
        backgroundColor: configData.style.backgroundColor,
        color: configData.style.color,
        fontFamily: configData.style.fontFamily,
      },
      enableVoiceOver: configData.enableVoiceOver,
      voiceOverValue: configData.voiceOverValue,
      voiceOverAudioFile: configData.voiceOverAudioFile,
      callOutWidth: configData.callOutWidth,
      callOutPosition: configData.callOutPosition,
      animation: configData.animation,
    });
  };

  const isTextEmpty = html => {
    if (!html) return true;

    const text = html
      .replace(/<[^>]*>/g, "") // remove tags
      .replace(/&nbsp;/g, " ") // remove nbsp
      .trim();

    return text === "";
  };

  const insertCallOut = () => {
    if (
      document.querySelectorAll(`#${metadata.activePageId} .dhp-root-widget[data-asset-type="${CALLOUT}"]`).length > 0
    )
      return; // only one callout in one page

    const htmlString = ReactDOMServer.renderToString(
      <CalloutView calloutTextConfig={calloutTextConfig} calloutWidthConfig={calloutWidthConfig} />
    );

    const elem = document.createElement("div");
    elem.innerHTML = htmlString;

    // callout arrow disable based on active page position
    if (metadata.activePageIdx === 0) {
      elem.querySelector(".icon-ui-arrow-left2").classList.add("disable");
    }
    if (metadata.activePageIdx === pageNodes.length - 1) {
      elem.querySelector(".icon-ui-arrow-right2").classList.add("disable");
    }

    // callout circle effect color based on circle color
    const calloutCircleElem = elem.querySelector(".callout-circle");
    calloutCircleElem.style.setProperty("--before-bg-color", calloutTextConfig.style.backgroundColor);

    // add scale in to callout div
    elem.querySelector(".callout").style.transform = "scale(1)";
    elem.querySelector(".callout").style.transformOrigin = "left top";
    elem.querySelector(".callout").style.width = `${
      ["Left", "Right"].includes(calloutTextConfig.callOutPosition)
        ? parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth]) + 60
        : parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth])
    }px`;

    // add pheudo elem in editor
    const parent = document.getElementById(metadata.activeBlockId);
    const pheudoElem = document.createElement("div");
    pheudoElem.id = "pheudoElem";
    pheudoElem.style.width = calloutWidthConfig[calloutTextConfig.callOutWidth];
    pheudoElem.style.visibility = "hidden";
    pheudoElem.innerHTML = htmlString;
    parent.appendChild(pheudoElem);

    // insert into editor
    setTimeout(() => {
      const jsonString = JSON.stringify(calloutTextConfig);
      let paramObj = { "data-config": jsonString };
      let dataAttr = { ...widgetConfig[CALLOUT].dataAttr, ...paramObj };
      let calculatedHeight = ["Bottom"].includes(calloutTextConfig.callOutPosition)
        ? document.querySelector("#pheudoElem .callout")?.offsetHeight + 60
        : document.querySelector("#pheudoElem .callout")?.offsetHeight;
      let calculatedWidth = ["Left", "Right"].includes(calloutTextConfig.callOutPosition)
        ? parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth]) + 60
        : parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth]);

      document.querySelector("#pheudoElem").remove();

      setCustomCss({
        width: `${calculatedWidth}px`,
        height: `${calculatedHeight}px`,
      }); // set width height to inner div

      setWidgetWidth(calculatedWidth); // set width  to main div
      setWidgetHeight(calculatedHeight); // set height to main div
      setAssetInnerContent(elem);
      setDataParam(dataAttr);
      setAddWidgetClicked(true);
      setIsDisable(true);
    }, 50);
  };

  const updateCallOut = () => {
    const activeElemScale = document.querySelector(`#${metadata.activeWidgetId[0]} .callout`).style.transform;
    const { scale } = getCssTransformObj({
      transform: document.querySelector(`#${metadata.activeWidgetId[0]} .callout`).style.transform,
    });

    const htmlString = ReactDOMServer.renderToString(
      <CalloutView calloutTextConfig={calloutTextConfig} calloutWidthConfig={calloutWidthConfig} />
    );
    const elem = document.createElement("div");
    elem.innerHTML = htmlString;

    // callout arrow disable based on active page position
    if (metadata.activePageIdx === 0) {
      elem.querySelector(".icon-ui-arrow-left2").classList.add("disable");
    }
    if (metadata.activePageIdx === pageNodes.length - 1) {
      elem.querySelector(".icon-ui-arrow-right2").classList.add("disable");
    }

    // callout circle effect color based on circle color
    const calloutCircleElem = elem.querySelector(".callout-circle");
    calloutCircleElem.style.setProperty("--before-bg-color", calloutTextConfig.style.backgroundColor);

    // add scale in to callout div
    elem.querySelector(".callout").style.transform = "scale(1)";
    elem.querySelector(".callout").style.transformOrigin = "left top";
    elem.querySelector(".callout").style.width = `${
      ["Left", "Right"].includes(calloutTextConfig.callOutPosition)
        ? parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth]) + 60
        : parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth])
    }px`;

    // add pheudo elem in editor
    const parent = document.getElementById(metadata.activeBlockId);
    const pheudoElem = document.createElement("div");
    pheudoElem.id = "pheudoElem";
    pheudoElem.style.width = calloutWidthConfig[calloutTextConfig.callOutWidth];
    pheudoElem.style.visibility = "hidden";
    pheudoElem.innerHTML = htmlString;
    parent.appendChild(pheudoElem);

    setTimeout(() => {
      const jsonString = JSON.stringify(calloutTextConfig);

      let calculatedHeight = ["Bottom"].includes(calloutTextConfig.callOutPosition)
        ? document.querySelector("#pheudoElem .callout")?.offsetHeight + 60
        : document.querySelector("#pheudoElem .callout")?.offsetHeight;
      let calculatedWidth = ["Left", "Right"].includes(calloutTextConfig.callOutPosition)
        ? parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth]) + 60
        : parseInt(calloutWidthConfig[calloutTextConfig.callOutWidth]);

      document.querySelector("#pheudoElem").remove();
      document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).innerHTML = elem.innerHTML;
      document.querySelector(`#${metadata.activeWidgetId[0]} .callout`).style.transform = activeElemScale;

      const targetWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);
      let newArray = Object.assign([...widgets]);
      newArray = Object.assign([...newArray], {
        [targetWidgetIndex]: {
          ...newArray[targetWidgetIndex],
          data: {
            ...newArray[targetWidgetIndex].data,
            "data-config": jsonString,
          },
          style: {
            ...newArray[targetWidgetIndex].style,
            width: `${calculatedWidth * scale.x}px`,
            height: `${calculatedHeight * scale.x}px`,
          },
          innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
        },
      });
      updateWidgets(newArray);
    }, 50);
    setIsDisable(true);
  };

  return (
    <div className={cx(style["editor-asset-inner"], style["call-out-asset"])}>
      <AssetName handleWidgetAction={props.handleWidgetAction} assetName={props.assetName} />

      <Nav tabs className={cx(style["line-tabs"], style["me-20"])}>
        <NavItem>
          <NavLink className={activeTab === 0 ? "active" : ""} onClick={() => setActiveTab(0)}>
            Text
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink className={activeTab === 1 ? "active" : ""} onClick={() => setActiveTab(1)}>
            Settings
          </NavLink>
        </NavItem>
      </Nav>

      {/* call out text */}
      {activeTab === 0 && (
        <CalloutText
          calloutTextConfig={calloutTextConfig}
          setCalloutTextConfig={setCalloutTextConfig}
          setIsGeneratingAudio={setIsGeneratingAudio}
        />
      )}

      {/* call out settings */}
      {activeTab === 1 && (
        <CalloutSettings calloutTextConfig={calloutTextConfig} setCalloutTextConfig={setCalloutTextConfig} />
      )}

      {/* button section */}
      {!isTextEmpty(calloutTextConfig?.text) && (
        <div className={cx(style["mt-auto"], style["text-center"], style["fixed-bottom"])}>
          {metadata.activeWidgetId.length === 1 &&
          metadata.activeWidgetId[0] &&
          metadata.activeWidgetType[0] === CALLOUT ? (
            <span
              className={cx(
                style["custom-tooltip"],
                style["btn"],
                {
                  [style["btn-primary"]]: !isDisable,
                },
                {
                  [style["btn-secondary"]]: isDisable,
                },
                {
                  [style["disable"]]: isDisable,
                }
              )}
              onClick={updateCallOut}>
              Update Callout
              {isGeneratingAudio && (
                <>
                  <div className={cx(style["custom-tooltip-content"], style["top"])}>
                    Generating voiceover for your text…
                  </div>
                  <ProcessLoader isOpen={true} />
                </>
              )}
            </span>
          ) : (
            <span
              className={cx(
                style["custom-tooltip"],
                style["btn"],
                {
                  [style["btn-primary"]]:
                    document.querySelectorAll(
                      `#${metadata.activePageId} .dhp-root-widget[data-asset-type="${CALLOUT}"]`
                    ).length === 0 && !isGeneratingAudio,
                },
                {
                  [style["btn-secondary"]]:
                    document.querySelectorAll(
                      `#${metadata.activePageId} .dhp-root-widget[data-asset-type="${CALLOUT}"]`
                    ).length > 0 || isGeneratingAudio,
                },
                {
                  [style["disable"]]:
                    document.querySelectorAll(
                      `#${metadata.activePageId} .dhp-root-widget[data-asset-type="${CALLOUT}"]`
                    ).length > 0 || isGeneratingAudio,
                }
              )}
              onClick={insertCallOut}>
              Insert Callout
              <div className={cx(style["custom-tooltip-content"], style["top"])}>
                {isGeneratingAudio ? "Generating voiceover for your text…" : "You can add only one Callout per page"}
              </div>
              {isGeneratingAudio && <ProcessLoader isOpen={true} />}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
CallOut.propTypes = {
  handleWidgetAction: PropTypes.func.isRequired,
  assetName: PropTypes.string.isRequired,
  assetType: PropTypes.string.isRequired,
};

export default CallOut;
